import { SEOLayout } from './SEOLayout';

const About = () => (
  <SEOLayout
    title="About Vergo | Our Mission to Help You Find the Right AI Tool"
    description="Learn about Vergo — an independent AI tool discovery platform helping people and teams find, compare, and adopt the best AI software for real work." keywords="about Vergo, AI tool directory, AI comparison platform, independent AI reviews"
  >
    <article className="prose prose-invert max-w-3xl mx-auto px-4 py-16">
      <h1>About Vergo</h1>
      <p className="lead">
        Vergo is an independent discovery and comparison platform for artificial intelligence
        software. We exist because the AI landscape moves faster than any single person, team, or
        publication can track — and the result is overwhelmed users, wasted budgets, and tools
        adopted for the wrong reasons.
      </p>

      <h2>Why we started Vergo</h2>
      <p>
        In 2023, choosing an AI tool meant comparing five chatbots. By 2026, there are thousands
        of AI products spanning writing, coding, design, research, video, audio, customer support,
        and dozens of vertical industries. Marketing pages all sound identical. Reddit threads
        contradict each other. Most "Top 10" lists are sponsored.
      </p>
      <p>
        We built Vergo to be the place we wished existed: a clean, fast, opinionated catalog of
        real AI tools, with hands-on testing, transparent methodology, and side-by-side
        comparisons that actually help you decide.
      </p>

      <h2>What we do</h2>
      <ul>
        <li><strong>Catalog 150+ AI tools</strong> across 25+ categories — from foundation-model chatbots to niche workflow utilities.</li>
        <li><strong>Compare tools side-by-side</strong> on capability, pricing, privacy, integrations, and real-world fit.</li>
        <li><strong>Recommend tools</strong> based on your task, role, and constraints through our AI Tool Finder quiz.</li>
        <li><strong>Track changes</strong> — pricing updates, new features, and deprecations are reviewed regularly.</li>
        <li><strong>Publish original guides</strong> on how to choose, evaluate, and combine AI tools effectively.</li>
      </ul>

      <h2>Who we are</h2>
      <p>
        Vergo is built by a small, distributed team of software engineers, product designers, and
        former technology journalists. We have collectively shipped products at startups and
        enterprise SaaS companies, and we have spent the last several years using AI tools daily
        in real production work. Our writing reflects that experience — not press releases.
      </p>

      <h2>How we make money</h2>
      <p>
        Vergo is free for users. We sustain the platform through three transparent revenue
        streams:
      </p>
      <ol>
        <li>
          <strong>Optional Pro subscription</strong> for power users who want unlimited AI-powered
          recommendations, deep comparison reports, and saved workspaces.
        </li>
        <li>
          <strong>Affiliate commissions</strong> from a small subset of tool vendors when readers
          sign up through our links. Affiliate status never affects ratings, ranking, or whether a
          tool is included.
        </li>
        <li>
          <strong>Display advertising</strong> served by independent ad networks. Ads are clearly
          labelled and never written or influenced by vendors.
        </li>
      </ol>
      <p>
        We do not accept paid placements, paid reviews, or "sponsored top spots." Read our full
        editorial standards on the <a href="/how-we-review">How We Review</a> page.
      </p>

      <h2>Our principles</h2>
      <ul>
        <li><strong>Independence</strong> — vendors do not get to preview or edit our reviews.</li>
        <li><strong>Accuracy</strong> — when we are wrong, we correct it publicly and quickly.</li>
        <li><strong>Clarity</strong> — plain language, no AI hype, no buzzwords for their own sake.</li>
        <li><strong>Privacy</strong> — we collect the minimum data needed to run the product. See our <a href="/privacy-policy">Privacy Policy</a>.</li>
      </ul>

      <h2>Get in touch</h2>
      <p>
        Found a tool we should add? Spotted an inaccuracy? Want to work with us? Reach us via the{' '}
        <a href="/contact">contact page</a>. We read every message.
      </p>
    </article>
  </SEOLayout>
);

export default About;
